import { currentLocale } from '@/lib/locale';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  CheckCircle, XCircle, Package, Laptop, Ban, ShoppingCart,
  ExternalLink, MapPin, Calendar, User, Cpu, Building2, Inbox, Trash2,
} from 'lucide-react';
import { useQuipteamsOrderStatuses } from '@/hooks/useApprovals';
import { trpc } from '@/lib/trpc';
import { useMemo } from 'react';
import type { ApprovalContext, ApprovalForPurchase } from '../../../../shared/types';
import { ApprovalContextPanel } from './ApprovalContextPanel';
import { WinclapLoader } from '@/components/ui/winclap-loader';

type TFunction = ReturnType<typeof useTranslation>['t'];

// Función de `t`: los textos salen de i18n y `t` no existe a nivel de módulo.
const QUIPTEAMS_STATUS_CONFIG = (t: TFunction): Record<string, { label: string; color: string }> => ({
  pending:     { label: t('approvals.actions.pendiente'),    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  in_progress: { label: t('approvals.actions.en_proceso'),   color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  delivered:   { label: t('approvals.actions.entregado'),    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  completed:   { label: t('approvals.actions.completado'),   color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  cancelled:   { label: t('approvals.actions.cancelado'),    color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
});

type QuipteamsStatusInfo = { status: string; trackingLink?: string; error?: string };

function QuipteamsStatusBadge({ info, isLoading }: { info: QuipteamsStatusInfo | undefined; isLoading: boolean }) {
  const { t } = useTranslation();

  if (isLoading) return <WinclapLoader size={14} className="text-foreground/25" />;
  // 'unknown' = QuipTeams no respondió (429/404/caída): sin dato, no un error.
  if (!info || info.status === 'unknown') {
    return <span className="text-xs text-muted-foreground" title={t('approvals.actions.estado_no_disponible')}>—</span>;
  }
  const data = info;

  const cfg = QUIPTEAMS_STATUS_CONFIG(t)[data.status] ?? { label: data.status, color: 'bg-gray-100 text-gray-700' };

  return (
    <div className="flex flex-col gap-1">
      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full w-fit ${cfg.color}`}>
        {cfg.label}
      </span>
      {data.trackingLink && (
        <a href={data.trackingLink} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 text-xs text-indigo-600 hover:underline">
          Tracking <ExternalLink className="h-2.5 w-2.5" />
        </a>
      )}
    </div>
  );
}

const STATUS_ACCENT: Record<string, string> = {
  DRAFT:     'border-l-blue-400',
  PENDING:   'border-l-yellow-400',
  APPROVED:  'border-l-green-400',
  PURCHASED: 'border-l-purple-400',
  REJECTED:  'border-l-red-400',
  CANCELLED: 'border-l-gray-300',
};

// Función de `t`: los textos salen de i18n y `t` no existe a nivel de módulo.
const STATUS_BADGE = (t: TFunction): Record<string, { label: string; className: string }> => ({
  DRAFT:     { label: t('approvals.actions.revision_people'), className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  PENDING:   { label: t('approvals.actions.revision_finanzas'), className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' },
  APPROVED:  { label: t('approvals.actions.aprobado'),       className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  PURCHASED: { label: t('approvals.actions.comprado'),       className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  REJECTED:  { label: t('approvals.actions.rechazado'),      className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  CANCELLED: { label: t('approvals.actions.cancelado'),      className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
});

function Initials({ name }: { name: string }) {
  const parts = name.trim().split(' ');
  const initials = parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-sm">
      {initials}
    </div>
  );
}

type WorkflowStage = 'tech' | 'finance' | 'approved' | 'completed';

interface ApprovalTableProps {
  approvals: ApprovalForPurchase[];
  isLoading: boolean;
  workflowStage?: WorkflowStage;
  onConfirmNeeded?: (approval: ApprovalForPurchase) => void;
  onConfirmNotNeeded?: (approval: ApprovalForPurchase) => void;
  onApprove?: (approval: ApprovalForPurchase) => void;
  onReject?: (approval: ApprovalForPurchase) => void;
  onMarkPurchased?: (approval: ApprovalForPurchase) => void;
  onOrderFromQuipteams?: (approval: ApprovalForPurchase) => void;
  onCancel?: (approval: ApprovalForPurchase) => void;
  onViewDetails?: (approval: ApprovalForPurchase) => void;
  /** Contexto por approvalId (oficina/stock/sugerencia) — etapas tech y finance. */
  context?: Record<string, ApprovalContext>;
}

export function ApprovalTable({
  approvals,
  isLoading,
  workflowStage,
  onConfirmNeeded,
  onConfirmNotNeeded,
  onApprove,
  onReject,
  onMarkPurchased,
  onOrderFromQuipteams,
  onCancel,
  context,
}: ApprovalTableProps) {
  const { t } = useTranslation();

  // Clapbase en vivo desde el community map (por email). El campo de la alta
  // (approval.clapbase) suele venir vacío; esto lo completa. React Query dedupe
  // la query aunque ApprovalTable se monte una vez por tab.
  const { data: communityRaw } = trpc.community.getMembers.useQuery({}, { staleTime: 5 * 60 * 1000 });

  // Estado de QuipTeams: una sola consulta por pantalla con todos los ids (17/09:
  // una por fila hacía 20+ requests a la vez y QuipTeams devolvía 429 a todas).
  // Solo las 40 compras más recientes con cotización: el historial completo crece para
  // siempre y una entrega de hace meses no necesita estado en vivo (el backend además
  // corta a los 15 s, lo que no entra se muestra con guion).
  const quoteIds = useMemo(() => {
    if (workflowStage !== 'completed') return [];
    const time = (a: ApprovalForPurchase) => Date.parse(a.updatedAt ?? a.createdAt ?? '') || 0;
    return [...approvals]
      .filter((a) => !!a.quipteamsQuoteId)
      .sort((a, b) => time(b) - time(a))
      .slice(0, 40)
      .map((a) => a.quipteamsQuoteId as string);
  }, [approvals, workflowStage]);
  const { data: quoteStatuses, isLoading: quoteStatusesLoading } = useQuipteamsOrderStatuses(quoteIds);
  const memberByEmail = useMemo(() => {
    const members: any[] = (communityRaw as any)?.data || [];
    return Object.fromEntries(members.map((m: any) => [String(m.email).toLowerCase(), m]));
  }, [communityRaw]);

  if (isLoading) {
    return (
      <div className="divide-y">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-5 animate-pulse">
            <div className="flex gap-3">
              <div className="h-9 w-9 rounded-full bg-muted" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-40 rounded bg-muted" />
                <div className="h-3 w-56 rounded bg-muted" />
                <div className="h-3 w-32 rounded bg-muted" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (approvals.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <Inbox className="h-10 w-10 text-muted-foreground/40" />
        <p className="font-medium text-muted-foreground">{t('approvals.empty')}</p>
      </div>
    );
  }

  return (
    <div className="divide-y">
      {approvals.map((approval) => {
        const statusBadge = STATUS_BADGE(t)[approval.status] ?? { label: approval.status, className: 'bg-gray-100 text-gray-600' };
        const accentClass = STATUS_ACCENT[approval.status] ?? 'border-l-gray-200';

        return (
          <div
            key={approval.approvalId}
            className={`flex flex-col sm:flex-row gap-4 p-5 border-l-4 ${accentClass} hover:bg-muted/30 transition-colors`}
          >
            {/* Left: employee + device */}
            <div className="flex flex-1 gap-3 min-w-0">
              <Initials name={approval.employeeName || '?'} />

              <div className="flex-1 min-w-0 space-y-2.5">
                {/* Name + status */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold leading-tight">{approval.employeeName}</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusBadge.className}`}>
                    {statusBadge.label}
                  </span>
                </div>

                {/* Email */}
                <p className="text-xs text-muted-foreground font-mono truncate">{approval.employeeEmail}</p>

                {/* Metadata row */}
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {approval.area && (
                    <span className="flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      {approval.area}
                    </span>
                  )}
                  {approval.role && (
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {approval.role}
                    </span>
                  )}
                  {(() => {
                    const ctx = context?.[approval.approvalId];
                    const clapbase =
                      ctx?.clapbaseLive ||
                      memberByEmail[approval.employeeEmail?.toLowerCase()]?.clapbase ||
                      approval.clapbase;
                    if (!approval.country && !clapbase) return null;
                    return (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {approval.country}
                        {clapbase && `${approval.country ? ' · ' : ''}${clapbase}`}
                      </span>
                    );
                  })()}
                  {approval.startDate && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Inicio: {approval.startDate}
                    </span>
                  )}
                  <span className="flex items-center gap-1 text-muted-foreground/60">
                    <Calendar className="h-3 w-3" />
                    {new Date(approval.createdAt).toLocaleDateString(currentLocale())}
                  </span>
                </div>

                {/* Contexto del ingreso: oficina, stock, sugerencia de modelo */}
                {(workflowStage === 'tech' || workflowStage === 'finance') &&
                  context?.[approval.approvalId] && (
                    <ApprovalContextPanel
                      context={context[approval.approvalId]}
                      approvalId={approval.approvalId}
                      employeeEmail={approval.employeeEmail}
                      employeeName={approval.employeeName}
                    />
                  )}

                {/* Device info */}
                <div className="flex items-start gap-1.5 rounded-md bg-muted/50 px-2.5 py-2 w-fit max-w-full">
                  <Cpu className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium leading-tight">
                      {approval.deviceType || <span className="italic text-muted-foreground">{t('approvals.not_specified')}</span>}
                    </p>
                    {approval.deviceSpecs && (
                      <p className="text-xs text-muted-foreground leading-tight mt-0.5 truncate max-w-[300px]">
                        {approval.deviceSpecs}
                      </p>
                    )}
                    {approval.estimatedCost && (
                      <p className="text-xs text-muted-foreground leading-tight mt-0.5">
                        {approval.currency} {approval.estimatedCost.toLocaleString(currentLocale())}
                      </p>
                    )}
                  </div>
                </div>

                {/* Responsible people */}
                {(approval.techAssignedName || approval.financeAssignedName) && (
                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                    {approval.techAssignedName && (
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        People: <span className="font-medium text-foreground">{approval.techAssignedName}</span>
                      </span>
                    )}
                    {approval.financeAssignedName && (
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        Finanzas: <span className="font-medium text-foreground">{approval.financeAssignedName}</span>
                      </span>
                    )}
                  </div>
                )}

                {/* Quipteams status (completed tab) */}
                {workflowStage === 'completed' && (
                  <div className="flex items-center gap-2">
                    {approval.quipteamsQuoteId ? (
                      <>
                        <QuipteamsStatusBadge info={quoteStatuses?.[approval.quipteamsQuoteId]} isLoading={quoteStatusesLoading} />
                        {approval.quipteamsKitName && (
                          <span className="text-xs text-muted-foreground">{approval.quipteamsKitName}</span>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">{t('approvals.actions.sin_pedido_a_quipteams')}</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right: actions */}
            <div className="flex sm:flex-col items-start sm:items-end justify-end gap-2 shrink-0 self-center sm:self-start sm:pt-1">
              {workflowStage === 'tech' && approval.status === 'DRAFT' && (
                <>
                  {onConfirmNeeded && (
                    <Button
                      size="sm"
                      onClick={() => onConfirmNeeded(approval)}
                      className="bg-blue-600 hover:bg-blue-700 gap-1.5"
                    >
                      <Laptop className="h-3.5 w-3.5" />
                      {t('approvals.actions.needs_hardware')}
                    </Button>
                  )}
                  {onConfirmNotNeeded && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onConfirmNotNeeded(approval)}
                      className="gap-1.5 text-muted-foreground"
                    >
                      <Ban className="h-3.5 w-3.5" />
                      {t('approvals.actions.no_hardware')}
                    </Button>
                  )}
                </>
              )}

              {workflowStage === 'finance' && approval.status === 'PENDING' && (
                <>
                  {onApprove && (
                    <Button
                      size="sm"
                      onClick={() => onApprove(approval)}
                      className="bg-green-600 hover:bg-green-700 gap-1.5"
                    >
                      <CheckCircle className="h-3.5 w-3.5" />
                      {t('approvals.actions.approve')}
                    </Button>
                  )}
                  {onReject && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onReject(approval)}
                      className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                    >
                      <XCircle className="h-3.5 w-3.5" />
                      {t('approvals.actions.reject')}
                    </Button>
                  )}
                </>
              )}

              {workflowStage === 'approved' && approval.status === 'APPROVED' && (
                onOrderFromQuipteams ? (
                  <Button
                    size="sm"
                    onClick={() => onOrderFromQuipteams(approval)}
                    className="bg-indigo-600 hover:bg-indigo-700 gap-1.5"
                  >
                    <ShoppingCart className="h-3.5 w-3.5" />
                    {t('approvals.actions.pedir_a_quipteams')}
                  </Button>
                ) : onMarkPurchased ? (
                  <Button
                    size="sm"
                    onClick={() => onMarkPurchased(approval)}
                    className="bg-blue-600 hover:bg-blue-700 gap-1.5"
                  >
                    <Package className="h-3.5 w-3.5" />
                    {t('approvals.actions.mark_purchased')}
                  </Button>
                ) : null
              )}

              {workflowStage === 'completed' && (
                <span className="text-xs text-muted-foreground italic">
                  {t('approvals.actions.completed')}
                </span>
              )}

              {/* Cancel — available in tech/finance/approved stages */}
              {onCancel && workflowStage !== 'completed' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onCancel(approval)}
                  className="gap-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 mt-1"
                  title={t('approvals.actions.recuperamos_el_equipo_cancelar_solicitud')}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Cancelar
                </Button>
              )}
            </div>
          </div>
        );
      })}

      {approvals.length > 0 && (
        <div className="px-5 py-3 text-xs text-muted-foreground">
          {approvals.length !== 1
            ? t('approvals.showing_plural', { count: approvals.length })
            : t('approvals.showing_one', { count: approvals.length })}
        </div>
      )}
    </div>
  );
}
